/**
 * CatalogPicker — search + multi-select over a paginated backend list.
 *
 * Used by the marketplace pack composer wizards to pick from the
 * SYSTEM-owned catalog of Players / Teams / Umpires / Stadiums.
 *
 * Props:
 *   - fetchFn({ search, page }) : promise resolving to axios response with
 *       DRF page shape { count, next, previous, results }.
 *   - selectedIds  : string[]   — currently picked row IDs.
 *   - onChange     : (ids)      — called with the new full ID list.
 *   - renderRow    : (row)      — react node for one catalog list row.
 *   - renderChip   : (row)      — compact node for one selected chip
 *                                 (a small X button is added automatically).
 *   - placeholder  : string     — search input placeholder.
 *   - label        : string     — small header ("Players", "Umpires", ...).
 *   - emptyHint    : string     — shown when catalog is empty for the
 *                                 current search.
 *   - pageSize     : number     — pass-through to the backend if it
 *                                 respects ?page_size= (default 50).
 *
 * The catalog fetches on mount, debounces search (300ms), and offers a
 * "Load more" button when `next` is set. Picking a row that's already
 * selected removes it (toggle). The selected chips render at the top so
 * the curator can see the current selection while scrolling the list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, X, Plus, Check } from "lucide-react";

export default function CatalogPicker({
  fetchFn,
  selectedIds,
  onChange,
  onToggle,
  renderRow,
  renderChip,
  placeholder = "Search catalog…",
  label = "",
  emptyHint = "Nothing matches this search.",
  pageSize = 50,
}) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Keep a lookup for the currently-selected rows so the chips render
  // with real names even if the row scrolled off the current page.
  const [byId, setById] = useState({});

  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  // Debounced search — 300ms after last keystroke.
  const searchRef = useRef(search);
  searchRef.current = search;
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchRef.current), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async ({ nextPage, replace }) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchFn({
        search: debouncedSearch,
        page: nextPage,
        page_size: pageSize,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      setById((prev) => {
        const next = { ...prev };
        for (const r of results) next[String(r.id)] = r;
        return next;
      });
      setRows((prev) => (replace ? results : [...prev, ...results]));
      setHasMore(Boolean(data?.next));
      setTotal(Number(data?.count) || 0);
      setPage(nextPage);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "failed to load");
    } finally {
      setLoading(false);
    }
  }, [fetchFn, debouncedSearch, pageSize]);

  useEffect(() => {
    fetchPage({ nextPage: 1, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  function toggle(row) {
    const id = String(row.id);
    const next = new Set(selectedSet);
    const nowOn = !next.has(id);
    if (nowOn) next.add(id);
    else next.delete(id);
    onChange(Array.from(next));
    if (onToggle) onToggle(row, nowOn);
  }

  function removeChip(id) {
    const next = selectedIds.filter((x) => String(x) !== String(id));
    onChange(next);
  }

  const selectedRows = (selectedIds || []).map((id) => byId[String(id)]).filter(Boolean);

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-text-secondary">
          <span>{label}</span>
          <span className="font-mono normal-case tracking-normal">
            {selectedIds.length} selected
            {total > 0 && ` · ${total} in catalog`}
          </span>
        </div>
      )}

      {selectedRows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5">
          {selectedRows.map((r) => (
            <span
              key={r.id}
              className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-full pl-2 pr-1 py-0.5"
            >
              {renderChip ? renderChip(r) : String(r.name || r.id)}
              <button
                type="button"
                onClick={() => removeChip(r.id)}
                className="rounded-full hover:bg-emerald-500/25 p-0.5"
                aria-label={`Remove ${r.name || r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-7 pr-3 py-2 text-sm rounded-md border border-border-default bg-bg-secondary focus:outline-none focus:border-button-primary"
        />
      </div>

      <div className="rounded-md border border-border-default bg-bg-secondary/40 max-h-80 overflow-y-auto">
        {error && (
          <div className="px-3 py-2 text-sm text-rose-300">{String(error)}</div>
        )}
        {rows.length === 0 && !loading && !error && (
          <div className="px-3 py-4 text-sm text-text-secondary text-center">
            {emptyHint}
          </div>
        )}
        {rows.map((r) => {
          const id = String(r.id);
          const on = selectedSet.has(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggle(r)}
              className={`w-full text-left px-3 py-1.5 text-sm border-t border-border-default first:border-t-0 flex items-center gap-2 hover:bg-bg-secondary ${on ? "bg-emerald-500/10" : ""}`}
            >
              <span className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${on ? "bg-emerald-500 border-emerald-500 text-white" : "border-border-default"}`}>
                {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 opacity-40" />}
              </span>
              <span className="min-w-0 flex-1">{renderRow(r)}</span>
            </button>
          );
        })}
        {hasMore && !loading && (
          <button
            type="button"
            onClick={() => fetchPage({ nextPage: page + 1, replace: false })}
            className="w-full py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-secondary border-t border-border-default"
          >
            Load more
          </button>
        )}
        {loading && (
          <div className="px-3 py-2 text-xs text-text-secondary flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> loading…
          </div>
        )}
      </div>
    </div>
  );
}
